import React, { useLayoutEffect, useRef, useState } from 'react';

import { SCREEN_SIZES } from '@/shared/config/ui';

import LaneLines from '@/features/laneChooser/components/lane/LaneLines';
import LaneShape from '@/features/laneChooser/components/lane/LaneShape';

export type LaneLayerProps = {
	topWidthPct?: number;
	bottomWidthPct?: number;
	laneLinesCount?: number;
};

const MOBILE_LANE_LINE_BOTTOM_WIDTH = 18;
const DESKTOP_LANE_LINE_BOTTOM_WIDTH = 30;

const LaneLayer = React.forwardRef<HTMLDivElement, LaneLayerProps>(
	({ laneLinesCount: laneLinesCountProp = 4, topWidthPct = 35, bottomWidthPct = 100 }, ref) => {
		const laneLayerRef = useRef<HTMLDivElement | null>(null);
		const [vbSize, setVbSize] = useState<{ w: number; h: number }>({
			w: 1,
			h: 1,
		});

		useLayoutEffect(() => {
			const el = laneLayerRef.current;
			if (!el) return;
			const update = () => {
				const r = el.getBoundingClientRect();
				setVbSize({ w: Math.max(1, r.width), h: Math.max(1, r.height) });
			};
			update();
			let ro: ResizeObserver | null = null;
			if (typeof ResizeObserver !== 'undefined') {
				ro = new ResizeObserver(() => update());
				ro.observe(el);
			} else {
				window.addEventListener('resize', update);
			}
			return () => {
				if (ro) ro.disconnect();
				else window.removeEventListener('resize', update);
			};
		}, []);

		// Merge forwarded ref with internal ref
		const setRefs = (node: HTMLDivElement | null) => {
			laneLayerRef.current = node;
			if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
		};

		// Clamp sensible range for perspective widths
		const bottomW = Math.max(18, bottomWidthPct);
		// top width should be <= bottom width; also clamp to a sensible minimum
		const unclampedTop = Math.max(5, Math.min(70, topWidthPct));
		const topW = Math.min(unclampedTop, Math.max(5, bottomW - 1));

		// Build a trapezoid lane spanning full height: narrow at top and wider towards bottom
		// Coordinates based on dynamic viewBox (container size)
		const vbWidth = vbSize.w;
		const vbHeight = vbSize.h;

		// Define top and bottom Y to span the full height of the wrapper
		const topY = 0;
		const bottomY = vbHeight;

		// Compute widths from percentages of total width
		const bottomWidthPx = (vbWidth * bottomW) / 100;
		const topWidthPx = (vbWidth * topW) / 100;
		const halfBottom = bottomWidthPx / 2;
		const halfTop = topWidthPx / 2;

		const centerX = vbWidth / 2;

		// Build inner guide lines positioned to divide the lane into N equal-width lanes.
		// Legacy defaults: N=4 renders center + two inner lines at 25%/75% of width.
		const innerTopOffset = halfTop * 0.5;
		const innerBottomOffset = halfBottom * 0.5;

		// Dynamic lane lines based on laneLinesCount
		const laneCount = Math.max(1, Math.min(4, Math.round(laneLinesCountProp || 4)));
		let laneOffsets: { topX: number; bottomX: number }[] | undefined = undefined;
		if (laneCount > 1) {
			laneOffsets = [];
			for (let i = 1; i <= laneCount - 1; i++) {
				const f = i / laneCount; // fraction across lane width from left edge
				const topX = centerX + (f - 0.5) * topWidthPx;
				const bottomX = centerX + (f - 0.5) * bottomWidthPx;
				laneOffsets.push({ topX, bottomX });
			}
		}

		const laneLineBottomWidth =
			window.innerWidth <= SCREEN_SIZES.MOBILE ? MOBILE_LANE_LINE_BOTTOM_WIDTH : DESKTOP_LANE_LINE_BOTTOM_WIDTH;

		return (
			<div
				ref={setRefs}
				className="lane-chooser__lane-layer"
				style={{
					position: 'absolute',
					inset: 0,
					zIndex: 2, // between top layers (1) and bottom foreground content
					pointerEvents: 'none',
					['--lane-top-width' as any]: `${topW}%`,
				}}
			>
				<svg
					viewBox={`0 0 ${vbWidth} ${vbHeight}`}
					xmlns="http://www.w3.org/2000/svg"
					width="100%"
					height="100%"
					preserveAspectRatio="xMidYMid slice"
					aria-hidden
				>
					<LaneShape vbWidth={vbWidth} vbHeight={vbHeight} topWidthPct={topW} bottomWidthPct={bottomW} />

					<LaneLines
						topY={topY}
						bottomY={bottomY}
						centerX={centerX}
						innerTopOffset={innerTopOffset}
						innerBottomOffset={innerBottomOffset}
						offsets={laneOffsets}
						bottomWidth={laneLineBottomWidth}
					/>
				</svg>
			</div>
		);
	},
);

export default LaneLayer;
